import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, ImageIcon, Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import { api, BACKEND_BASE } from "@/lib/api";
import { CATEGORY_COLORS, fmtMoney } from "@/lib/theme";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";

function b64ToDataUrl(b64) {
  if (!b64) return "";
  if (b64.startsWith("data:")) return b64;
  const mime = b64.startsWith("iVBORw0KGgo") ? "image/png" : "image/jpeg";
  return `data:${mime};base64,${b64}`;
}

export default function SharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [viewImg, setViewImg] = useState(null); // dataUrl
  const [imgLoading, setImgLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api(`/share/${token}/data`, { auth: false });
      setData(d);
    } catch (e) {
      if (e?.status === 410) setError("This report link has expired.");
      else if (e?.status === 404) setError("This report link is invalid or was revoked.");
      else setError(e?.message || "Couldn't load report.");
    }
  }, [token]);

  useEffect(() => {
    document.title = "RigBooks · Shared Expense Report";
    load();
  }, [load]);

  const openImage = async (rid) => {
    setImgLoading(true);
    setViewImg("");
    try {
      const r = await api(`/share/${token}/image/${rid}`, { auth: false });
      setViewImg(b64ToDataUrl(r.image_base64));
    } catch {
      setViewImg(null);
    } finally {
      setImgLoading(false);
    }
  };

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-rig-bg px-6 text-center">
        <Logo size={40} />
        <p className="font-display text-2xl uppercase tracking-wide text-white">{error}</p>
        <a href="/" className="text-rig-accent hover:underline">Go to RigBooks</a>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-rig-bg">
        <Loader2 className="h-6 w-6 animate-spin text-rig-accent" />
      </div>
    );
  }

  const cats = Object.entries(data.totals_by_category || {}).filter(([, v]) => v > 0);

  return (
    <div className="min-h-screen bg-rig-bg">
      {/* Header */}
      <header className="border-b border-rig-border bg-rig-surface">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3 px-6 py-5">
          <Logo size={34} />
          <a
            href={`${BACKEND_BASE}/api/share/${token}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-download-pdf"
          >
            <Button className="bg-rig-accent font-display text-xs font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark">
              <Download className="mr-2 h-4 w-4" /> Download PDF
            </Button>
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-8">
        <div className="flex items-center gap-2 text-rig-accent">
          <ShieldCheck className="h-4 w-4" />
          <span className="font-display text-xs font-bold uppercase tracking-[0.2em]">Read-only audit report</span>
        </div>
        <h1 className="mt-2 font-display text-4xl font-bold uppercase tracking-wide text-white">
          {data.label}
        </h1>
        <p className="mt-1 text-rig-dim">
          {data.driver?.name || data.driver?.email} · {data.year} · expires {String(data.expires_at).slice(0, 10)}
        </p>

        <div className="mt-6 rounded-2xl border border-rig-accent/50 bg-rig-surface p-6">
          <div className="font-display text-xs font-bold uppercase tracking-widest text-rig-accent">
            Total Deductible Expenses
          </div>
          <div className="mt-1 font-display text-5xl font-bold text-white">{fmtMoney(data.grand_total)}</div>
          <div className="mt-4 flex flex-wrap gap-2">
            {cats.map(([c, v]) => (
              <span
                key={c}
                className="rounded-full px-3 py-1 text-xs font-semibold"
                style={{ background: `${CATEGORY_COLORS[c] || "#94A3B8"}22`, color: CATEGORY_COLORS[c] || "#94A3B8" }}
              >
                {c} · {fmtMoney(v)}
              </span>
            ))}
          </div>
        </div>

        <h2 className="mt-8 font-display text-sm font-bold uppercase tracking-[0.18em] text-rig-dim">
          Itemized Receipts ({data.receipts.length})
        </h2>
        <div className="mt-3 overflow-hidden rounded-2xl border border-rig-border">
          <table className="w-full text-left">
            <thead className="bg-rig-surface2">
              <tr className="font-display text-[11px] uppercase tracking-widest text-rig-dim">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Vendor</th>
                <th className="hidden px-4 py-3 sm:table-cell">Category</th>
                <th className="hidden px-4 py-3 md:table-cell">Location</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {data.receipts.map((r) => (
                <tr key={r.id} className="border-t border-rig-border bg-rig-surface">
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-rig-dim">{r.receipt_date}</td>
                  <td className="px-4 py-3 font-semibold text-white">{r.vendor}</td>
                  <td className="hidden px-4 py-3 sm:table-cell">
                    <span style={{ color: CATEGORY_COLORS[r.category] || "#94A3B8" }} className="text-sm font-semibold">
                      {r.category}
                    </span>
                  </td>
                  <td className="hidden px-4 py-3 text-sm text-rig-dim md:table-cell">{r.gps_label || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-display text-lg font-bold text-white">
                    {fmtMoney(r.amount)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {r.has_image && (
                      <button
                        onClick={() => openImage(r.id)}
                        className="text-rig-accent hover:text-rig-accentDark"
                        title="View receipt image"
                      >
                        <ImageIcon className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Viral CTA */}
        <div className="mt-10 flex flex-col items-center justify-between gap-4 rounded-2xl border border-rig-border bg-rig-surface p-6 sm:flex-row">
          <div>
            <div className="font-display text-lg font-bold uppercase tracking-wide text-white">
              Manage other drivers?
            </div>
            <p className="text-sm text-rig-dim">Get RigBooks in front of your other clients — audit-ready books in one tap.</p>
          </div>
          <a href="/register">
            <Button className="bg-rig-accent font-display text-xs font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark">
              Get RigBooks <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </a>
        </div>
      </main>

      <Dialog open={viewImg !== null} onOpenChange={(o) => !o && setViewImg(null)}>
        <DialogContent className="max-w-lg border-rig-border bg-rig-surface">
          {imgLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-rig-accent" />
            </div>
          ) : viewImg ? (
            <img src={viewImg} alt="receipt" className="max-h-[70vh] w-full rounded-lg object-contain" />
          ) : (
            <p className="py-10 text-center text-rig-dim">Image unavailable.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
